import {
  array,
  enum as zEnum,
  number,
  object,
  string,
  ZodIssueCode,
  type RefinementCtx,
} from 'zod';

import {
  IS_MEMBERSHIP_LIMIT_LESS_THAN_GLOBAL,
  isGreaterThanOrEqualTo,
} from '@occc-app/lib/constants/validationMessages';
import { withIsEnabled } from '@occc/validation';

import { AWARD_COUNT_LIMIT_MAX_ROWS } from './award-count-limit.constants';

const AWARD_COUNT_LIMIT_WHEN_ENABLED_MESSAGE =
  'At least one limit is required when the section is enabled';

export const AWARD_COUNT_LIMIT_NO_DUPLICATE_PERIODS_MESSAGE =
  'Duplicate time periods are not allowed';

export const awardCountLimitTimePeriodSchema = zEnum(['TOTAL', 'DAY', 'WEEK', 'MONTH']);

export const awardCountLimitRowSchema = object({
  id: string().optional(),
  limit: number({ required_error: isGreaterThanOrEqualTo(1) })
    .int()
    .min(1, isGreaterThanOrEqualTo(1)),
  timePeriod: awardCountLimitTimePeriodSchema,
});

const AwardCountLimitEnabledSchema = object({
  rows: array(awardCountLimitRowSchema)
    .min(1, AWARD_COUNT_LIMIT_WHEN_ENABLED_MESSAGE)
    .max(AWARD_COUNT_LIMIT_MAX_ROWS)
    .superRefine((rows, ctx) => {
      const seen = new Set<string>();
      rows.forEach((row, i) => {
        if (seen.has(row.timePeriod)) {
          ctx.addIssue({
            code: 'custom',
            message: AWARD_COUNT_LIMIT_NO_DUPLICATE_PERIODS_MESSAGE,
            path: [i, 'timePeriod'],
          });
        } else {
          seen.add(row.timePeriod);
        }
      });
    }),
});

const AwardCountLimitDisabledSchema = object({}).passthrough();

export const awardCountLimitSchema = withIsEnabled(
  AwardCountLimitEnabledSchema,
  AwardCountLimitDisabledSchema,
).optional();

export const awardCountLimitFormSchema = awardCountLimitSchema;

type ComparableAwardCountLimit =
  | { isEnabled?: boolean; rows?: Array<{ limit?: number; timePeriod?: string }> }
  | null
  | undefined;

export const getMembershipAwardCountLimitViolations = (
  membership: ComparableAwardCountLimit,
  global: ComparableAwardCountLimit,
): number[] => {
  if (!membership?.isEnabled || !global?.isEnabled) return [];

  const globalLimitByPeriod = new Map<string, number>();
  (global.rows ?? []).forEach(({ limit, timePeriod }) => {
    if (timePeriod != null && limit != null) {
      globalLimitByPeriod.set(timePeriod, limit);
    }
  });

  const violations: number[] = [];
  (membership.rows ?? []).forEach(({ limit, timePeriod }, index) => {
    if (timePeriod == null || limit == null) return;
    const globalLimit = globalLimitByPeriod.get(timePeriod);
    if (globalLimit != null && limit >= globalLimit) {
      violations.push(index);
    }
  });

  return violations;
};

/**
 * Zod refinement wrapper around {@link getMembershipAwardCountLimitViolations}. Emits an
 * issue on each violating Per Member row limit. Paths are relative to the awardCount object,
 * matching where this runs from (the enabled schema's superRefine).
 */
export const validateMembershipAwardCountLimitsLessThanGlobal = ({
  membership,
  global,
  ctx,
}: {
  membership: ComparableAwardCountLimit;
  global: ComparableAwardCountLimit;
  ctx: RefinementCtx;
}) => {
  getMembershipAwardCountLimitViolations(membership, global).forEach((index) => {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: IS_MEMBERSHIP_LIMIT_LESS_THAN_GLOBAL,
      path: ['membershipAwardCountLimits', 'rows', index, 'limit'],
    });
  });
};
